function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t["return"] && (u = t["return"](), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import React, { Suspense } from "react";
import GlobalStyle from "@styles/globalStyle";
import { ToastifyGlobalStyle } from "@styles/toastifyGlobalStyle";
import { ThemeProvider, ToastContainer } from "@vinomofo/components";
import { defaultToolkitTheme } from "@styles/defaultToolkitTheme";
import ErrorNotifierProvider from "@context/error-notifier/provider";
import { Auth0Provider } from "@auth0/auth0-react";
import { AUTH0_DOMAIN, AUTH0_CLIENT_ID, AUTH0_AUDIENCE } from "@/src/config";
import { BrowserRouter } from "react-router-dom";
import { ModalProvider } from "./context/modal/provider";
import { isOrderConfirmationPage } from "./utils/is-order-confirmation-page";
import { PaymentProvider } from "./context/payment/provider";
import { getStorefrontHomeUrl } from "./utils/storefront-redirect";
import { navigateToOrderConfirmation } from "./utils/navigate-to-order-confirmation";

// BC bounces logged-in shoppers with a completed cart to this route
// server-side. It isn't part of our SPA surface, so we redirect away
// before mounting anything. Wallet-button success flows can also land
// here briefly; gate the redirect on `window.checkoutConfig` (only
// injected by BC's own server-rendered page) so we don't eat them.
var BC_INTERNAL_ORDER_CONFIRMATION_PATH = "/checkout/order-confirmation";
var CheckoutProvider = /*#__PURE__*/React.lazy(function () {
  return import("@context/checkout/provider");
});
var Checkout = /*#__PURE__*/React.lazy(function () {
  return import("./components/checkout/Checkout").then(function (module) {
    return {
      "default": module.Checkout
    };
  });
});
var OrderConfirmationPage = /*#__PURE__*/React.lazy(function () {
  return import("./pages/OrderConfirmationPage");
});

// Start the heavy checkout-sdk download as this module evaluates (during
// build.js eval) so it overlaps Auth0 init + the provider/Checkout lazy
// chunks, instead of waiting for a post-mount effect. Only the SDK leaf is
// warmed (not app modules) to avoid the eager-eval issues that reverted the
// earlier broad warm. Skipped on the confirmation route, which doesn't use it.
var checkoutSdkPromise = typeof window !== "undefined" && !isOrderConfirmationPage() ? import("@bigcommerce/checkout-sdk") : null;
var AsyncCheckoutProvider = function AsyncCheckoutProvider(_ref) {
  var children = _ref.children;
  var _React$useState = React.useState(null),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    checkoutService = _React$useState2[0],
    setCheckoutService = _React$useState2[1];
  React.useEffect(function () {
    (checkoutSdkPromise !== null && checkoutSdkPromise !== void 0 ? checkoutSdkPromise : import("@bigcommerce/checkout-sdk")).then(function (_ref2) {
      var createCheckoutService = _ref2.createCheckoutService;
      setCheckoutService(createCheckoutService());
    });
  }, []);
  if (!checkoutService) return null;
  return /*#__PURE__*/React.createElement(Suspense, {
    fallback: null
  }, /*#__PURE__*/React.createElement(CheckoutProvider, {
    checkoutService: checkoutService
  }, children));
};
var onRedirectCallback = function onRedirectCallback(appState) {
  if (appState !== null && appState !== void 0 && appState.returnTo) {
    window.location.replace(appState.returnTo);
  } else {
    window.history.replaceState({}, document.title, window.location.pathname);
  }
};
var App = function App() {
  var redirectUri = typeof window !== "undefined" ? window.location.origin + window.location.pathname : "".concat(process.env.STOREFRONT_HOST, "/auth-callback?redirectToCheckout=true");
  if (typeof window !== "undefined" && window.location.pathname === BC_INTERNAL_ORDER_CONFIRMATION_PATH && typeof window.checkoutConfig !== "undefined") {
    var config = window.checkoutConfig;
    var checkoutId = config === null || config === void 0 ? void 0 : config.checkoutId;
    var orderId = config === null || config === void 0 ? void 0 : config.orderId;
    if (checkoutId || orderId) {
      var timestamp = Date.now();
      var isGuest = localStorage.getItem("isGuest") === "yes";
      var dob = localStorage.getItem("dob") || undefined;
      var isOptIn = localStorage.getItem("emailOptIn") === "true";
      var navigate = function navigate(cartId) {
        navigateToOrderConfirmation({
          checkoutId: cartId,
          isGuest: isGuest,
          dob: dob,
          isOptIn: isOptIn,
          siteBrand: "vm"
        });
      };
      if (checkoutId) {
        navigate(checkoutId);
      } else {
        // BigCommerce only returns the order to the session that placed it. The
        // cart id proves order ownership to our API, so no endpoint hands it out.
        (checkoutSdkPromise !== null && checkoutSdkPromise !== void 0 ? checkoutSdkPromise : import("@bigcommerce/checkout-sdk")).then(function (_ref3) {
          var createCheckoutService = _ref3.createCheckoutService;
          return createCheckoutService().loadOrder(Number(orderId));
        }).then(function (state) {
          var _state$data$getOrder;
          var cartId = (_state$data$getOrder = state.data.getOrder()) === null || _state$data$getOrder === void 0 ? void 0 : _state$data$getOrder.cartId;
          if (cartId) {
            navigate(cartId);
          } else {
            throw new Error("Missing cartId on order");
          }
        })["catch"](function (err) {
          // Fallback to home if resolution fails
          var target = getStorefrontHomeUrl(null, true) || process.env.STOREFRONT_HOST || "/";
          window.location.replace(target);
        });
      }
      return null;
    }
    var target = getStorefrontHomeUrl(null, true) || process.env.STOREFRONT_HOST || "/";
    if (target && target !== window.location.href) {
      window.location.replace(target);
    }
    return null;
  }
  return /*#__PURE__*/React.createElement(ErrorNotifierProvider, null, /*#__PURE__*/React.createElement(BrowserRouter, null, /*#__PURE__*/React.createElement(Auth0Provider, {
    domain: AUTH0_DOMAIN,
    clientId: AUTH0_CLIENT_ID,
    authorizationParams: {
      redirect_uri: redirectUri,
      audience: AUTH0_AUDIENCE
    },
    cacheLocation: "localstorage",
    useRefreshTokens: true,
    useRefreshTokensFallback: true,
    onRedirectCallback: onRedirectCallback
  }, /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: defaultToolkitTheme
  }, /*#__PURE__*/React.createElement(ModalProvider, null, /*#__PURE__*/React.createElement(GlobalStyle, null), /*#__PURE__*/React.createElement(ToastifyGlobalStyle, null), /*#__PURE__*/React.createElement(ToastContainer, {
    position: "top-right"
  }), /*#__PURE__*/React.createElement(Suspense, {
    fallback: /*#__PURE__*/React.createElement("div", null)
  }, isOrderConfirmationPage() ? /*#__PURE__*/React.createElement(OrderConfirmationPage, null) : /*#__PURE__*/React.createElement(AsyncCheckoutProvider, null, /*#__PURE__*/React.createElement(PaymentProvider, null, /*#__PURE__*/React.createElement(Checkout, null)))))))));
};
export default App;